import { FC } from 'react';
import clsx from 'clsx';
import { ActivityIndicator } from '@kontentino/ui';
import NumberUtil from 'utils/number';
import MetricHeader from './MetricHeader';

interface MetricStatTileProps {
  label: string;
  value: number | null;
  changePercent: number | null;
  caption?: string;
  isLoading?: boolean;
  dataCy: string;
  /** Interactive (selectable) tile rendered as a button, e.g. the overview pills. */
  interactive?: boolean;
  isActive?: boolean;
  onClick?: () => void;
}

/**
 * Shared metric tile — a bordered box wrapping a `MetricHeader`, so the title,
 * value, badge, and caption are identical to every other metric header. Used by
 * the overview summary (interactive pills) and the report summary (static
 * boxes); the only differences are the box (interactive vs static).
 */
const MetricStatTile: FC<MetricStatTileProps> = ({
  label,
  value,
  changePercent,
  caption,
  isLoading = false,
  dataCy,
  interactive = false,
  isActive = false,
  onClick,
}) => {
  const header = isLoading ? (
    <>
      <MetricHeader title={label} />
      <span className="tw-flex tw-h-7 tw-items-center">
        <ActivityIndicator className="tw-text-grayscale-100" />
      </span>
    </>
  ) : (
    <MetricHeader
      title={label}
      value={value != null ? NumberUtil.compact(value) : '—'}
      badge={changePercent ?? undefined}
      caption={caption}
    />
  );

  if (interactive) {
    return (
      <button
        type="button"
        onClick={onClick}
        data-cy={dataCy}
        className={clsx(
          'tw-flex tw-flex-col tw-items-start tw-gap-2 tw-rounded-xl tw-border tw-bg-grayscale-5 tw-p-4 tw-text-left tw-transition-colors',
          {
            'tw-border-primary-100 tw-bg-grayscale-20': isActive,
            'tw-border-grayscale-5 hover:tw-border-grayscale-10': !isActive,
          },
        )}
      >
        {header}
      </button>
    );
  }

  return (
    <div
      data-cy={dataCy}
      className="tw-flex tw-flex-col tw-items-start tw-gap-2 tw-rounded-xl tw-border tw-border-grayscale-10 tw-bg-grayscale-5 tw-p-4"
    >
      {header}
    </div>
  );
};

export default MetricStatTile;
